import axios from 'axios';

export class WindService {
    constructor() {
        this.apiKey = import.meta.env.VITE_OPENWEATHER_API_KEY || null;
        this.backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
        this.useBackend = import.meta.env.VITE_USE_BACKEND === 'true';
        this.currentWeatherUrl = 'https://api.openweathermap.org/data/2.5/weather';
        this.forecastUrl = 'https://api.openweathermap.org/data/2.5/forecast';
        this.useRealAPI = this.useBackend || (this.apiKey && this.apiKey !== 'your_openweather_api_key_here');
        
        if (this.useBackend) {
            console.log('✓ Using Lambda backend for wind data');
        } else if (this.useRealAPI) {
            console.log('✓ Using OpenWeatherMap API directly (with forecast support)');
        } else {
            console.log('ℹ Using mock wind data (add API key for real data)');
        }
    }

    /**
     * Get wind data for multiple points along a route
     * @param {Array} coordinates - Array of {lat, lng} objects
     * @param {number} samples - Number of wind samples to get
     */
    async getWindAlongRoute(coordinates, samples = 10) {
        try {
            // Sample points along the route
            const samplePoints = this.sampleRoutePoints(coordinates, samples);
            
            if (this.useRealAPI) {
                let windPromises;
                if (this.useBackend) {
                    // Route through Lambda to keep API key server-side
                    const response = await fetch(`${this.backendUrl}/api/wind`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ locations: samplePoints })
                    });
                    const data = await response.json();
                    return data.results || [];
                } else {
                    windPromises = samplePoints.map(point =>
                        this.getWindAtLocation(point.lat, point.lng)
                    );
                    return await Promise.all(windPromises);
                }
            } else {
                return this.generateMockWindData(samplePoints);
            }
        } catch (error) {
            console.error('Error fetching wind data:', error);
            console.log('Falling back to mock wind data');
            return this.generateMockWindData(this.sampleRoutePoints(coordinates, samples));
        }
    }

    /**
     * Get wind data for a specific location
     */
    async getWindAtLocation(lat, lng) {
        try {
            const response = await axios.get(this.currentWeatherUrl, {
                params: {
                    lat: lat,
                    lon: lng,
                    appid: this.apiKey,
                    units: 'imperial'
                }
            });

            return {
                lat: lat,
                lng: lng,
                speed: response.data.wind.speed,
                direction: response.data.wind.deg,
                gust: response.data.wind.gust || response.data.wind.speed,
                description: response.data.weather[0].description
            };
        } catch (error) {
            console.error('Error fetching wind at location:', error);
            throw error;
        }
    }

    /**
     * Get wind forecast at a specific location and time
     * @param {number} lat - Latitude
     * @param {number} lng - Longitude
     * @param {Date|string} timestamp - When the user will be at this location
     * @returns {Object} Wind forecast data
     */
    async getWindForecastAtTime(lat, lng, timestamp) {
        try {
            const targetTime = new Date(timestamp);
            
            if (this.useRealAPI) {
                if (this.useBackend) {
                    // Route through Lambda
                    const response = await fetch(`${this.backendUrl}/api/wind/forecast`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                            lat, 
                            lng, 
                            timestamp: targetTime.toISOString() 
                        })
                    });
                    const data = await response.json();
                    return data;
                } else {
                    // Call OpenWeatherMap forecast API directly
                    const response = await axios.get(this.forecastUrl, {
                        params: {
                            lat: lat,
                            lon: lng,
                            appid: this.apiKey,
                            units: 'imperial'
                        }
                    });

                    // Find the forecast closest to target time
                    const forecast = this.findClosestForecast(response.data.list, targetTime);
                    
                    return {
                        lat: lat,
                        lng: lng,
                        speed: forecast.wind.speed,
                        direction: forecast.wind.deg,
                        gust: forecast.wind.gust || forecast.wind.speed,
                        description: forecast.weather[0].description,
                        forecastTime: forecast.dt_txt,
                        targetTime: targetTime.toISOString()
                    };
                }
            } else {
                // Mock forecast data
                return this.generateMockWindForecast(lat, lng, targetTime);
            }
        } catch (error) {
            console.error('Error fetching wind forecast:', error);
            // Fall back to mock data
            return this.generateMockWindForecast(lat, lng, new Date(timestamp));
        }
    }

    /**
     * Find the forecast entry closest to the target time
     */
    findClosestForecast(forecastList, targetTime) {
        let closest = forecastList[0];
        let minDiff = Math.abs(new Date(forecastList[0].dt * 1000) - targetTime);

        for (const forecast of forecastList) {
            const forecastTime = new Date(forecast.dt * 1000);
            const diff = Math.abs(forecastTime - targetTime);
            
            if (diff < minDiff) {
                minDiff = diff;
                closest = forecast;
            }
        }

        return closest;
    }

    /**
     * Generate mock wind forecast for demo
     */
    generateMockWindForecast(lat, lng, targetTime) {
        const baseSpeed = 8 + Math.random() * 15;
        const direction = Math.floor(Math.random() * 360);
        const gust = baseSpeed + Math.random() * 8;

        return {
            lat: lat,
            lng: lng,
            speed: Math.round(baseSpeed * 10) / 10,
            direction: direction,
            gust: Math.round(gust * 10) / 10,
            description: this.getWindDescription(baseSpeed),
            forecastTime: targetTime.toISOString(),
            targetTime: targetTime.toISOString()
        };
    }

    /**
     * Calculate arrival times at each waypoint along the route
     * @param {Date|string} departureTime - When the trip starts
     * @param {Array} waypoints - Array of waypoints with coordinates and distances
     * @param {Object} travelPreferences - User's travel preferences
     * @returns {Array} Waypoints with arrival times
     */
    calculateWaypointArrivalTimes(departureTime, waypoints, travelPreferences = {}) {
        // Default travel preferences (from spec)
        const defaults = {
            avgCruisingSpeedMph: 60,
            fuelStopDurationMinutes: 30,
            mealStopDurationMinutes: 45,
            restStopDurationMinutes: 15
        };
        
        const prefs = { ...defaults, ...travelPreferences };
        let currentTime = new Date(departureTime);
        
        return waypoints.map((waypoint, index) => {
            if (index === 0) {
                // First waypoint is departure point
                return {
                    ...waypoint,
                    arrivalTime: currentTime.toISOString(),
                    distanceFromPrevious: 0
                };
            }
            
            // Calculate travel time from previous waypoint
            const distanceMiles = waypoint.distanceFromPrevious || 0;
            const travelHours = distanceMiles / prefs.avgCruisingSpeedMph;
            
            // Add travel time — this is when you ARRIVE at this point
            currentTime = new Date(currentTime.getTime() + (travelHours * 60 * 60 * 1000));
            
            // Record arrival time BEFORE adding stop duration
            // Wind at this location matters when you arrive, not when you leave
            const arrivalTime = currentTime.toISOString();
            
            // Add stop duration AFTER recording arrival — only affects next waypoint's timing
            if (waypoint.stopType === 'fuel') {
                currentTime = new Date(currentTime.getTime() + (prefs.fuelStopDurationMinutes * 60 * 1000));
            } else if (waypoint.stopType === 'meal') {
                currentTime = new Date(currentTime.getTime() + (prefs.mealStopDurationMinutes * 60 * 1000));
            } else if (waypoint.stopType === 'rest') {
                currentTime = new Date(currentTime.getTime() + (prefs.restStopDurationMinutes * 60 * 1000));
            } else if (waypoint.stopType === 'custom' && waypoint.stopDurationMinutes) {
                currentTime = new Date(currentTime.getTime() + (waypoint.stopDurationMinutes * 60 * 1000));
            }
            
            return {
                ...waypoint,
                arrivalTime
            };
        });
    }

    /**
     * Build time-aware wind profile for a route
     * @param {Date|string} departureTime - When the trip starts
     * @param {Array} waypoints - Route waypoints with coordinates
     * @param {Object} rvSpecs - RV specifications for wind threshold
     * @param {Object} travelPreferences - User's travel preferences
     * @returns {Object} Wind profile with time-aware forecasts
     */
    async buildRouteWindProfile(departureTime, waypoints, rvSpecs = {}, travelPreferences = {}) {
        try {
            // Calculate arrival times at each waypoint
            const timedWaypoints = this.calculateWaypointArrivalTimes(
                departureTime,
                waypoints,
                travelPreferences
            );
            
            // Batch all waypoints into a single API call to avoid rate limiting
            let windResults;
            if (this.useBackend) {
                // Send all waypoints to batch endpoint — one Lambda call, sequential OpenWeather calls server-side
                const batchPayload = timedWaypoints.map(wp => ({
                    lat: wp.lat,
                    lng: wp.lng,
                    timestamp: wp.arrivalTime
                }));
                
                try {
                    const response = await fetch(`${this.backendUrl}/api/wind/forecast-batch`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ waypoints: batchPayload })
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        windResults = data.results || [];
                    } else {
                        console.warn('Wind batch endpoint returned', response.status, '— falling back to individual calls');
                        windResults = null;
                    }
                } catch (err) {
                    console.warn('Wind batch endpoint failed — falling back to individual calls:', err.message);
                    windResults = null;
                }
            }
            
            // Build wind profile from batch results or fall back to individual calls
            const windProfile = [];
            const thresholds = this.getRigWindThreshold(rvSpecs);
            const gustCautionThreshold = thresholds.caution * 1.2;
            const gustDangerThreshold = thresholds.danger * 1.2;
            
            for (let i = 0; i < timedWaypoints.length; i++) {
                const waypoint = timedWaypoints[i];
                let wind;
                
                if (windResults && windResults[i]) {
                    // Use batch result
                    wind = {
                        speed: windResults[i].speed || 0,
                        direction: windResults[i].direction || 0,
                        gust: windResults[i].gust || 0,
                        description: windResults[i].description || '',
                        forecastTime: windResults[i].forecastTime || '',
                        targetTime: windResults[i].targetTime || waypoint.arrivalTime
                    };
                } else {
                    // Fallback to individual call (for non-backend or if batch failed)
                    wind = await this.getWindForecastAtTime(
                        waypoint.lat,
                        waypoint.lng,
                        waypoint.arrivalTime
                    );
                }
                
                const cautionExceeded = wind.speed >= thresholds.caution || wind.gust >= gustCautionThreshold;
                const dangerExceeded = wind.speed >= thresholds.danger || wind.gust >= gustDangerThreshold;
                
                windProfile.push({
                    ...waypoint,
                    wind,
                    cautionExceeded,
                    dangerExceeded,
                    cautionThreshold: thresholds.caution,
                    dangerThreshold: thresholds.danger,
                    gustCautionThreshold,
                    gustDangerThreshold
                });
            }
            
            // Find worst wind point
            const worstPoint = windProfile.reduce((worst, current) => {
                return current.wind.speed > worst.wind.speed ? current : worst;
            }, windProfile[0]);
            
            return {
                windProfile,
                hasCautions: windProfile.some(w => w.cautionExceeded),
                hasDangers: windProfile.some(w => w.dangerExceeded),
                worstPoint,
                maxWindSpeed: worstPoint.wind.speed,
                maxGustSpeed: worstPoint.wind.gust
            };
        } catch (error) {
            console.error('Error building wind profile:', error);
            throw error;
        }
    }

    /**
     * Get wind threshold for RV type
     * Based on published RV safety guidelines from industry experts and transportation authorities.
     * 
     * Sources:
     * - CampgroundViews.com: Travel trailers max safe gusts 30-40 mph, sustained winds >30 mph = don't drive
     * - EngineerFix.com: High-profile vehicles risk begins at 26 mph sustained
     * - CRST.com (trucking): Vehicles <25,000 lbs limited at 35+ mph
     * 
     * DISCLAIMER: These are general guidelines. Actual safe wind speeds vary based on:
     * - Wind direction (crosswinds are most dangerous)
     * - Road conditions and exposure
     * - Driver experience
     * - Specific vehicle weight and aerodynamics
     * - Presence of sway control and trailer brakes
     * 
     * Always use your judgment and err on the side of caution.
     * 
     * @param {Object} rvSpecs - RV specifications
     * @returns {Object} Thresholds for caution and danger zones
     */
    getRigWindThreshold(rvSpecs) {
        const height = rvSpecs.height || 12;
        const weight = rvSpecs.weight || null;
        const length = rvSpecs.length || null;
        const rvType = rvSpecs.type || rvSpecs.rvType || 'class-c';
        
        // Base thresholds by RV type (sustained wind speeds in mph)
        // Yellow (caution) = increased difficulty, drive carefully
        // Red (danger) = don't drive, high risk of loss of control
        const baseThresholds = {
            'truck-only': {
                caution: 35,
                danger: 45,
                typicalWeight: 6000,
                typicalLength: 20
            },
            'class-a': {
                caution: 25,
                danger: 35,
                typicalWeight: 25000,
                typicalLength: 35
            },
            'class-b': {
                caution: 30,
                danger: 40,
                typicalWeight: 9000,
                typicalLength: 21
            },
            'class-c': {
                caution: 28,
                danger: 38,
                typicalWeight: 12000,
                typicalLength: 28
            },
            'fifth-wheel': {
                caution: 26,
                danger: 35,
                typicalWeight: 12000,
                typicalLength: 32
            },
            'travel-trailer': {
                caution: 25,
                danger: 30,
                typicalWeight: 5000,
                typicalLength: 25
            },
            'truck-camper': {
                caution: 32,
                danger: 42,
                typicalWeight: 3000,
                typicalLength: 18
            }
        };
        
        const thresholds = baseThresholds[rvType] || baseThresholds['class-c'];
        
        // Start with base thresholds
        let cautionThreshold = thresholds.caution;
        let dangerThreshold = thresholds.danger;
        
        // 1. HEIGHT ADJUSTMENT (most significant factor)
        // Taller = more wind surface area = lower threshold
        // Each foot above 10ft reduces threshold by 3%
        const heightFactor = 1 - ((height - 10) * 0.03);
        cautionThreshold *= heightFactor;
        dangerThreshold *= heightFactor;
        
        // 2. WEIGHT ADJUSTMENT (very significant for stability)
        // Heavier = more stable = higher threshold
        // Lighter = easier to push = lower threshold
        if (weight && thresholds.typicalWeight) {
            const weightRatio = weight / thresholds.typicalWeight;
            
            // Weight adjustment: ±15% max
            // Lighter than typical: reduce threshold (more vulnerable)
            // Heavier than typical: increase threshold (more stable)
            let weightFactor = 1.0;
            
            if (weightRatio < 0.7) {
                // Very light (< 70% of typical): -15%
                weightFactor = 0.85;
            } else if (weightRatio < 0.85) {
                // Light (70-85% of typical): -10%
                weightFactor = 0.90;
            } else if (weightRatio < 0.95) {
                // Slightly light (85-95% of typical): -5%
                weightFactor = 0.95;
            } else if (weightRatio > 1.3) {
                // Very heavy (> 130% of typical): +15%
                weightFactor = 1.15;
            } else if (weightRatio > 1.15) {
                // Heavy (115-130% of typical): +10%
                weightFactor = 1.10;
            } else if (weightRatio > 1.05) {
                // Slightly heavy (105-115% of typical): +5%
                weightFactor = 1.05;
            }
            
            cautionThreshold *= weightFactor;
            dangerThreshold *= weightFactor;
        }
        
        // 3. LENGTH ADJUSTMENT (minor factor)
        // Longer = more surface area for wind = slightly lower threshold
        // But effect is smaller than height since wind hits side profile
        if (length && thresholds.typicalLength) {
            const lengthRatio = length / thresholds.typicalLength;
            
            // Length adjustment: ±5% max (less impact than height/weight)
            let lengthFactor = 1.0;
            
            if (lengthRatio > 1.3) {
                // Very long (> 130% of typical): -5%
                lengthFactor = 0.95;
            } else if (lengthRatio > 1.15) {
                // Long (115-130% of typical): -3%
                lengthFactor = 0.97;
            } else if (lengthRatio < 0.7) {
                // Very short (< 70% of typical): +5%
                lengthFactor = 1.05;
            } else if (lengthRatio < 0.85) {
                // Short (70-85% of typical): +3%
                lengthFactor = 1.03;
            }
            
            cautionThreshold *= lengthFactor;
            dangerThreshold *= lengthFactor;
        }
        
        const finalCautionThreshold = Math.round(cautionThreshold);
        const finalDangerThreshold = Math.round(dangerThreshold);
        
        console.log(`🌬️ Wind thresholds for ${rvType} (H:${height}ft, W:${weight}lbs, L:${length}ft): Caution ${finalCautionThreshold} mph, Danger ${finalDangerThreshold} mph`);
        
        return {
            caution: finalCautionThreshold,
            danger: finalDangerThreshold
        };
    }

    /**
     * Sample points evenly along a route
     */
    sampleRoutePoints(coordinates, samples) {
        if (coordinates.length <= samples) {
            return coordinates;
        }

        const sampledPoints = [];
        const step = (coordinates.length - 1) / (samples - 1);

        for (let i = 0; i < samples; i++) {
            const index = Math.round(i * step);
            sampledPoints.push(coordinates[index]);
        }

        return sampledPoints;
    }

    /**
     * Generate mock wind data for demo
     */
    generateMockWindData(coordinates) {
        return coordinates.map((coord, index) => {
            // Generate realistic wind data
            const baseSpeed = 8 + Math.random() * 15; // 8-23 mph
            const direction = Math.floor(Math.random() * 360);
            const gust = baseSpeed + Math.random() * 8;

            return {
                lat: coord.lat,
                lng: coord.lng,
                speed: Math.round(baseSpeed * 10) / 10,
                direction: direction,
                gust: Math.round(gust * 10) / 10,
                description: this.getWindDescription(baseSpeed)
            };
        });
    }

    /**
     * Get wind description based on speed
     */
    getWindDescription(speed) {
        if (speed < 5) return 'Light breeze';
        if (speed < 15) return 'Moderate wind';
        if (speed < 25) return 'Strong wind';
        if (speed < 35) return 'Very strong wind';
        return 'Dangerous wind conditions';
    }

    /**
     * Analyze wind impact on RV driving
     */
    analyzeWindImpact(windData, rvHeight) {
        const warnings = [];
        let maxWindSpeed = 0;
        let avgWindSpeed = 0;

        windData.forEach(point => {
            maxWindSpeed = Math.max(maxWindSpeed, point.speed, point.gust);
            avgWindSpeed += point.speed;

            // Generate warnings for high winds
            if (point.speed > 25 || point.gust > 30) {
                warnings.push({
                    location: `${point.lat.toFixed(2)}, ${point.lng.toFixed(2)}`,
                    speed: point.speed,
                    gust: point.gust,
                    severity: 'high',
                    message: 'Dangerous wind conditions for RVs. Consider delaying travel.'
                });
            } else if (point.speed > 15 || point.gust > 20) {
                warnings.push({
                    location: `${point.lat.toFixed(2)}, ${point.lng.toFixed(2)}`,
                    speed: point.speed,
                    gust: point.gust,
                    severity: 'moderate',
                    message: 'Moderate winds. Drive with caution, especially on exposed areas.'
                });
            }
        });

        avgWindSpeed = avgWindSpeed / windData.length;

        return {
            maxWindSpeed: Math.round(maxWindSpeed * 10) / 10,
            avgWindSpeed: Math.round(avgWindSpeed * 10) / 10,
            warnings: warnings,
            overallRisk: this.calculateWindRisk(maxWindSpeed, rvHeight)
        };
    }

    /**
     * Calculate wind risk level for RV
     */
    calculateWindRisk(windSpeed, rvHeight) {
        // Higher RVs are more affected by wind
        const heightFactor = rvHeight / 10;
        const adjustedSpeed = windSpeed * heightFactor;

        if (adjustedSpeed > 30) return 'high';
        if (adjustedSpeed > 20) return 'moderate';
        return 'low';
    }
}
